import strategy from 'react-validatorjs-strategy'
import type { CampaignSection, EmailElement } from 'types/content-editor'
import { LINK_VALIDATOR, validateLink } from '@disco/utils/links'

const traverseSection = (
  section: CampaignSection,
  func: (content: EmailElement) => Record<string, any> | null | undefined,
  isRule: boolean = false,
): Record<string, any> => {
  let validatorData: Record<string, any> = {}
  let isNeedRule = false
  section.columns.forEach((column, columnIndex) => {
    if (column.columnContent) {
      column.columnContent.forEach((content, contentIndex) => {
        const data = func(content)

        if (data?.isNeedRule) {
          isNeedRule = true
        }

        if (isRule && data) {
          validatorData = { ...data }
          return
        }

        if (!data) return
        validatorData = {
          ...validatorData,
          [columnIndex]: {
            ...validatorData[columnIndex],
            [contentIndex]: data,
          },
        }
      })
    }
  })

  if (isNeedRule) {
    validatorData.isNeedRule = isNeedRule
  }

  return validatorData
}

const getEmailElementValidatorData = (
  element: EmailElement,
): Record<string, any> | null | undefined => {
  if (element.type === 'button')
    return {
      link: element.link,
    }
  if (element.type === 'image')
    return {
      link: element.link,
      location: element.src,
    }
  if (
    element.type === 'footer' &&
    element.footer &&
    element.footer.social_links
  )
    return element.footer.social_links
  return null
}

const getEmailElementValidatorSchema = (
  element: EmailElement,
): Record<string, any> | null | undefined => {
  if (element.type === 'button')
    return {
      link: LINK_VALIDATOR,
    }
  if (element.type === 'image')
    return {
      link: LINK_VALIDATOR,
      location: 'url',
    }

  if (element.type === 'footer') {
    return {
      apple: 'url',
      facebook: 'url',
      instagram: 'url',
      soundcloud: 'url',
      spotify: 'url',
      twitter: 'url',
      web: 'url',
      youtube: 'url',
    }
  }

  return null
}

const getValidatorCustomRuleSchema = (element: EmailElement) =>
  ['button', 'image'].includes(element.type)
    ? {
        isNeedRule: true,
      }
    : {
        isNeedRule: false,
      }

const getValidatorRules = (section: CampaignSection) =>
  traverseSection(section, getEmailElementValidatorSchema)

export const getValidatorData = (section: CampaignSection): any =>
  traverseSection(section, getEmailElementValidatorData)

const isCustomRule = (section: CampaignSection) =>
  traverseSection(section, getValidatorCustomRuleSchema, true)

export const getValidatorTypes = (section: CampaignSection): any =>
  strategy.createSchema(
    getValidatorRules(section),
    {
      url: 'The url format is invalid.',
    },
    validator => {
      const { isNeedRule } = isCustomRule(section)

      if (isNeedRule) {
        validator.constructor.register(
          LINK_VALIDATOR,
          validateLink,
          'The url format is invalid.',
        )
      }
    },
  )
